'use client';

import React from 'react';
import { parseDate } from 'chrono-node';
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { ActiveModifiers } from 'react-day-picker';
import { Calendar, CalendarProps } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Button, buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Calendar as CalendarIcon, LucideTextCursorInput } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import moment from 'moment';
import { isTouchViewport, useIsMobile } from '@/hooks/use-mobile';
import { useMediaQuery } from '@/components/ui/hooks/use-media-query';
import { BottomSheet, BottomSheetTextButton } from '@/components/mobile';
import { touchHitArea } from '@/components/ui/touch-target';

/* -------------------------------------------------------------------------- */
/*                               Inspired By:                                 */
/*                               @steventey                                   */
/* ------------------https://dub.co/blog/smart-datetime-picker--------------- */
/* -------------------------------------------------------------------------- */

/**
 * Utility function that parses dates.
 * Parses a given date string using the `chrono-node` library.
 *
 * @param str - A string representation of a date and time.
 * @returns A `Date` object representing the parsed date and time, or `null` if the string could not be parsed.
 */
export const parseDateTime = (str: Date | string) => {
	if (str instanceof Date) return str;
	return parseDate(str);
};

/**
 * Converts a given timestamp or the current date and time to a string representation in the local time zone.
 * format: `HH:mm`, adjusted for the local time zone.
 *
 * @param timestamp {Date | string}
 * @returns A string representation of the timestamp
 */
export const getDateTimeLocal = (timestamp?: Date): string => {
	const d = timestamp ? new Date(timestamp) : new Date();
	if (d.toString() === 'Invalid Date') return '';
	return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
		.toISOString()
		.split(':')
		.slice(0, 2)
		.join(':');
};

/**
 * Formats a given date and time object or string into a human-readable string representation.
 * "MMM D, YYYY h:mm A" (e.g. "Jan 1, 2023 12:00 PM").
 *
 * @param datetime - {Date | string}
 * @returns A string representation of the date and time
 */
export const formatDateTime = (datetime: Date | string) => {
	return moment(datetime).format('ll LT');
};

const inputBase =
	'bg-transparent focus:outline-hidden focus:ring-0 focus-within:outline-hidden focus-within:ring-0 sm:text-sm disabled:cursor-not-allowed disabled:opacity-50';

// @source: https://www.perplexity.ai/search/in-javascript-how-RfI7fMtITxKr5c.V9Lv5KA#1
// use this pattern to validate the transformed date string for the natural language input
const naturalInputValidationPattern =
	'^[A-Z][a-z]{2}sd{1,2},sd{4},sd{1,2}:d{2}s[AP]M$';

const DEFAULT_SIZE = 96;

// A landscape phone (or any short window below 1280px): a popover under the
// field would only have room for a couple of week rows.
const SHORT_QUERY = '(max-width: 1279px) and (max-height: 500px)';

/**
 * Centres a time in the list's own scroll area. scrollIntoView would also
 * scroll the popover or bottom sheet around it, which on a phone hides the
 * calendar above.
 */
const scrollIntoList = (elm: HTMLElement) => {
	const list = elm.closest<HTMLElement>('[data-radix-scroll-area-viewport]');
	if (!list) return;
	const box = list.getBoundingClientRect();
	const rect = elm.getBoundingClientRect();
	list.scrollBy({
		top: rect.top - box.top - (box.height - rect.height) / 2,
		left: rect.left - box.left - (box.width - rect.width) / 2,
		behavior: 'smooth',
	});
};

/**
 * Smart time input Docs: {@link: https://shadcn-extension.vercel.app/docs/smart-time-input}
 */

interface SmartDatetimeInputProps {
	value?: Date;
	onValueChange: (date: Date) => void;
	// When true, initialize the Time list from the provided value.
	// Defaults to previous behavior when omitted.
	syncTimeFromValue?: boolean;
	dataTestid?: string;
}

interface SmartDatetimeInputContextProps extends SmartDatetimeInputProps {
	Time: string;
	onTimeChange: (time: string) => void;
}

const SmartDatetimeInputContext =
	React.createContext<SmartDatetimeInputContextProps | null>(null);

const useSmartDateInput = () => {
	const context = React.useContext(SmartDatetimeInputContext);
	if (!context) {
		throw new Error(
			'useSmartDateInput must be used within SmartDateInputProvider'
		);
	}
	return context;
};

export const SmartDatetimeInput = React.forwardRef<
	HTMLInputElement,
	Omit<
		React.InputHTMLAttributes<HTMLInputElement>,
		'type' | 'ref' | 'value' | 'defaultValue' | 'onBlur'
	> &
		SmartDatetimeInputProps
>(
	(
		{
			className,
			value,
			onValueChange,
			placeholder,
			disabled,
			dataTestid,
			syncTimeFromValue,
		},
		ref
	) => {
		// ? refactor to be only used with controlled input
		/*  const [dateTime, setDateTime] = React.useState<Date | undefined>(
    value ?? undefined
  ); */

		const [Time, setTime] = React.useState<string>('');

		const onTimeChange = React.useCallback((time: string) => {
			setTime(time);
		}, []);

		return (
			<SmartDatetimeInputContext.Provider
				value={{
					value,
					onValueChange,
					Time,
					onTimeChange,
					syncTimeFromValue,
				}}
			>
				<div className="flex items-center justify-center">
					<div
						className={cn(
							'flex gap-1 w-full p-1 items-center justify-between rounded-md border transition-all',
							'focus-within:outline-0 focus:outline-0 focus:ring-0',
							'placeholder:text-muted-foreground focus-visible:outline-0 ',
							className
						)}
					>
						<DateTimeLocalInput />
						<NaturalLanguageInput
							data-testid={dataTestid}
							placeholder={placeholder}
							disabled={disabled}
							ref={ref}
						/>
					</div>
				</div>
			</SmartDatetimeInputContext.Provider>
		);
	}
);

SmartDatetimeInput.displayName = 'DatetimeInput';

// Make it a standalone component

const TimePicker = () => {
	const { value, onValueChange, Time, onTimeChange } = useSmartDateInput();
	const [activeIndex, setActiveIndex] = React.useState(-1);
	const timestamp = 15;

	const formateSelectedTime = React.useCallback(
		(time: string, hour: number, partStamp: number) => {
			onTimeChange(time);

			const newVal = parseDateTime(value ?? new Date());

			if (!newVal) return;

			newVal.setHours(
				hour,
				partStamp === 0 ? parseInt('00') : timestamp * partStamp
			);

			// ? refactor needed check if we want to use the new date

			onValueChange(newVal);
		},
		[value]
	);

	const handleKeydown = React.useCallback(
		(e: React.KeyboardEvent<HTMLDivElement>) => {
			e.stopPropagation();

			if (!document) return;

			const moveNext = () => {
				const nextIndex =
					activeIndex + 1 > DEFAULT_SIZE - 1 ? 0 : activeIndex + 1;

				const currentElm = document.getElementById(`time-${nextIndex}`);

				currentElm?.focus();

				setActiveIndex(nextIndex);
			};

			const movePrev = () => {
				const prevIndex =
					activeIndex - 1 < 0 ? DEFAULT_SIZE - 1 : activeIndex - 1;

				const currentElm = document.getElementById(`time-${prevIndex}`);

				currentElm?.focus();

				setActiveIndex(prevIndex);
			};

			const setElement = () => {
				const currentElm = document.getElementById(
					`time-${activeIndex}`
				);

				if (!currentElm) return;

				currentElm.focus();

				const timeValue = currentElm.textContent ?? '';

				// this should work now haha that hour is what does the trick

				const PM_AM = timeValue.split(' ')[1];
				const PM_AM_hour = parseInt(
					timeValue.split(' ')[0].split(':')[0]
				);
				const hour =
					PM_AM === 'AM'
						? PM_AM_hour === 12
							? 0
							: PM_AM_hour
						: PM_AM_hour === 12
						? 12
						: PM_AM_hour + 12;

				const part = Math.floor(
					parseInt(timeValue.split(' ')[0].split(':')[1]) / 15
				);

				formateSelectedTime(timeValue, hour, part);
			};

			const reset = () => {
				const currentElm = document.getElementById(
					`time-${activeIndex}`
				);
				currentElm?.blur();
				setActiveIndex(-1);
			};

			switch (e.key) {
				case 'ArrowUp':
					movePrev();
					break;

				case 'ArrowDown':
					moveNext();
					break;

				case 'Escape':
					reset();
					break;

				case 'Enter':
					setElement();
					break;
			}
		},
		[activeIndex, formateSelectedTime]
	);

	const handleClick = React.useCallback(
		(hour: number, part: number, PM_AM: string, currentIndex: number) => {
			formateSelectedTime(
				`${hour}:${part === 0 ? '00' : timestamp * part} ${PM_AM}`,
				hour,
				part
			);
			setActiveIndex(currentIndex);
		},
		[formateSelectedTime]
	);

	const currentTime = React.useMemo(() => {
		const timeVal = Time.split(' ')[0];
		return {
			hours: parseInt(timeVal.split(':')[0]),
			minutes: parseInt(timeVal.split(':')[1]),
		};
	}, [Time]);

	React.useEffect(() => {
		const getCurrentElementTime = () => {
			const timeVal = Time.split(' ')[0];
			const hours = parseInt(timeVal.split(':')[0]);
			const minutes = parseInt(timeVal.split(':')[1]);
			const PM_AM = Time.split(' ')[1];

			const formatIndex =
				PM_AM === 'AM' ? hours : hours === 12 ? hours : hours + 12;
			const formattedHours = formatIndex;

			for (let j = 0; j <= 3; j++) {
				const diff = Math.abs(j * timestamp - minutes);
				const selected =
					PM_AM === (formattedHours >= 12 ? 'PM' : 'AM') &&
					(minutes <= 53
						? diff < Math.ceil(timestamp / 2)
						: diff < timestamp);

				if (selected) {
					const trueIndex =
						activeIndex === -1
							? formattedHours * 4 + j
							: activeIndex;

					setActiveIndex(trueIndex);

					const currentElm = document.getElementById(
						`time-${trueIndex}`
					);
					if (currentElm) scrollIntoList(currentElm);
				}
			}
		};

		getCurrentElementTime();
	}, [Time, activeIndex]);

	const height = React.useMemo(() => {
		if (!document) return;
		const calendarElm = document.getElementById('calendar');
		if (!calendarElm) return;
		return calendarElm.style.height;
	}, []);

	return (
		<div className="space-y-2 pr-3 py-3 relative max-md:border-t max-md:px-3">
			<h3 className="text-sm font-medium ">Time</h3>
			<ScrollArea
				type="always"
				onKeyDown={handleKeydown}
				className={cn(
					'h-[90%] w-full focus-visible:outline-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-0 py-0.5',
					// Phones: the row scrolls sideways by touch (Radix only scrolls
					// an axis that has a scrollbar) and the vertical bar goes.
					'max-md:[&>[data-radix-scroll-area-viewport]]:!overflow-x-auto max-md:[&>[data-radix-scroll-area-viewport]]:!overflow-y-hidden max-md:[&>[data-orientation=vertical]]:hidden'
				)}
				style={{
					height,
				}}
			>
				<ul
					role="listbox"
					aria-label="Time"
					className={cn(
						'flex items-center flex-col gap-1 h-full max-h-56 w-28 px-1 py-0.5',
						// Phones: one row of 44px times under the calendar, scrolled sideways.
						'max-md:max-h-none max-md:w-max max-md:flex-row'
					)}
				>
					{Array.from({ length: 24 }).map((_, i) => {
						const PM_AM = i >= 12 ? 'PM' : 'AM';
						const formatIndex =
							i > 12 ? i % 12 : i === 0 || i === 12 ? 12 : i;
						return Array.from({ length: 4 }).map((_, part) => {
							const diff = Math.abs(
								part * timestamp - currentTime.minutes
							);

							const trueIndex = i * 4 + part;

							// ? refactor : add the select of the default time on the current device (H:MM)
							const isSelected =
								(currentTime.hours === i ||
									currentTime.hours === formatIndex) &&
								Time.split(' ')[1] === PM_AM &&
								(currentTime.minutes <= 53
									? diff < Math.ceil(timestamp / 2)
									: diff < timestamp);

							const isSuggested = !value && isSelected;

							const currentValue = `${formatIndex}:${
								part === 0 ? '00' : timestamp * part
							} ${PM_AM}`;

							return (
								<li
									role="option"
									aria-selected={isSelected}
									tabIndex={isSelected ? 0 : -1}
									id={`time-${trueIndex}`}
									key={`time-${trueIndex}`}
									className={cn(
										buttonVariants({
											variant: isSuggested
												? 'secondary'
												: isSelected
												? 'default'
												: 'outline',
										}),
										'h-8 px-3 w-full text-sm focus-visible:outline-0 outline-0 focus-visible:border-0 cursor-default ring-0',
										'max-md:h-11 max-md:w-auto max-md:shrink-0'
									)}
									onClick={() =>
										handleClick(i, part, PM_AM, trueIndex)
									}
									onFocus={() =>
										isSuggested && setActiveIndex(trueIndex)
									}
								>
									{currentValue}
								</li>
							);
						});
					})}
				</ul>
			</ScrollArea>
		</div>
	);
};

const NaturalLanguageInput = React.forwardRef<
	HTMLInputElement,
	{
		placeholder?: string;
		disabled?: boolean;
	}
>(({ placeholder, ...props }, ref) => {
	const { value, onValueChange, Time, onTimeChange, syncTimeFromValue } =
		useSmartDateInput();

	const _placeholder =
		placeholder ?? 'e.g. "tomorrow at 5pm" or "in 2 hours"';

	const [inputValue, setInputValue] = React.useState<string>('');

	React.useEffect(() => {
		if (syncTimeFromValue && value) {
			const base = new Date(value);
			if (isNaN(base.getTime())) return;
			const hours24 = base.getHours();
			const minutes = base.getMinutes();
			const PM_AM = hours24 >= 12 ? 'PM' : 'AM';
			const hour12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
			const minuteStr = String(minutes).padStart(2, '0');
			setInputValue(formatDateTime(value));
			onTimeChange(`${hour12}:${minuteStr} ${PM_AM}`);
			return;
		}

		// Previous behavior preserved when prop not provided
		const hour = new Date().getHours();
		const timeVal = `${
			hour >= 12 ? hour % 12 : hour
		}:${new Date().getMinutes()} ${hour >= 12 ? 'PM' : 'AM'}`;
		setInputValue(value ? formatDateTime(value) : '');
		onTimeChange(value ? Time : timeVal);
	}, [value, Time, syncTimeFromValue]);

	const handleParse = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			// parse the date string when the input field loses focus
			const parsedDateTime = parseDateTime(e.currentTarget.value);
			if (parsedDateTime) {
				const PM_AM = parsedDateTime.getHours() >= 12 ? 'PM' : 'AM';
				//fix the time format for this value

				const PM_AM_hour = parsedDateTime.getHours();

				const hour =
					PM_AM_hour > 12
						? PM_AM_hour % 12
						: PM_AM_hour === 0 || PM_AM_hour === 12
						? 12
						: PM_AM_hour;

				onValueChange(parsedDateTime);
				setInputValue(formatDateTime(parsedDateTime));
				onTimeChange(`${hour}:${parsedDateTime.getMinutes()} ${PM_AM}`);
			}
		},
		[value]
	);

	const handleKeydown = React.useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			switch (e.key) {
				case 'Enter':
					const parsedDateTime = parseDateTime(e.currentTarget.value);
					if (parsedDateTime) {
						const PM_AM =
							parsedDateTime.getHours() >= 12 ? 'PM' : 'AM';
						//fix the time format for this value

						const PM_AM_hour = parsedDateTime.getHours();

						const hour =
							PM_AM_hour > 12
								? PM_AM_hour % 12
								: PM_AM_hour === 0 || PM_AM_hour === 12
								? 12
								: PM_AM_hour;

						onValueChange(parsedDateTime);
						setInputValue(formatDateTime(parsedDateTime));
						onTimeChange(
							`${hour}:${parsedDateTime.getMinutes()} ${PM_AM}`
						);
					}
					break;
			}
		},
		[value]
	);

	return (
		<Input
			ref={ref}
			type="text"
			placeholder={_placeholder}
			value={inputValue}
			onChange={(e) => {
				setInputValue(e.currentTarget.value);
				if (e.currentTarget.value.trim() === '') {
					onValueChange(null);
				}
			}}
			onKeyDown={handleKeydown}
			onBlur={handleParse}
			className={cn(
				// 40px on phones and touch screens (tablets, landscape phones)
				'px-2 mr-0.5 flex-1 border-none h-8 rounded max-md:h-10 touch:h-10',
				inputBase
			)}
			{...props}
		/>
	);
});

NaturalLanguageInput.displayName = 'NaturalLanguageInput';

type DateTimeLocalInputProps = {} & CalendarProps;

const DateTimeLocalInput = ({
	className,
	...props
}: DateTimeLocalInputProps) => {
	const { value, onValueChange, Time } = useSmartDateInput();

	const formateSelectedDate = React.useCallback(
		(
			date: Date | undefined,
			selectedDate: Date,
			m: ActiveModifiers,
			e: React.MouseEvent
		) => {
			const parsedDateTime = parseDateTime(selectedDate);

			if (parsedDateTime) {
				parsedDateTime.setHours(
					parseInt(Time.split(':')[0]),
					parseInt(Time.split(':')[1])
				);
				onValueChange(parsedDateTime);
			}
		},
		[value, Time]
	);

	const isMobile = useIsMobile();
	const isShort = useMediaQuery(SHORT_QUERY);
	const sheetFits = isMobile || isShort;
	const [open, setOpen] = React.useState(false);
	// Phones and short screens pick in a bottom sheet, where the calendar and
	// the times fit. Keep whichever it opened as until it closes, so rotating
	// mid-pick doesn't swap it.
	const [openedAsSheet, setOpenedAsSheet] = React.useState(sheetFits);
	if (!open && openedAsSheet !== sheetFits) setOpenedAsSheet(sheetFits);
	const asSheet = open ? openedAsSheet : sheetFits;
	const triggerRef = React.useRef<HTMLButtonElement>(null);

	const trigger = (
		<Button
			ref={triggerRef}
			type="button"
			variant={'outline'}
			size={'icon'}
			className={cn(
				// Phones and touch screens: 40px, inside a 44px hit area.
				'size-9 flex items-center justify-center font-normal max-md:size-10 touch:size-10',
				touchHitArea(undefined, { coarse: true }),
				!value && 'text-muted-foreground'
			)}
			// Only for the sheet: as a PopoverTrigger child, its own props would
			// override the trigger's.
			{...(asSheet && {
				'aria-haspopup': 'dialog' as const,
				'aria-expanded': open,
				onClick: () => setOpen(true),
			})}
		>
			<CalendarIcon className="size-4" />
			<span className="sr-only">Choose date and time</span>
		</Button>
	);

	const picker = (
		// Centred in a landscape phone's full-width sheet.
		<div className={cn('flex gap-1 max-md:flex-col', asSheet && 'md:mx-auto md:w-fit')}>
			<Calendar
				{...props}
				id={'calendar'}
				className={cn(
					'peer flex justify-end max-md:justify-center',
					inputBase,
					className
				)}
				mode="single"
				selected={value}
				onSelect={formateSelectedDate}
				initialFocus
			/>
			<TimePicker />
		</div>
	);

	if (asSheet) {
		return (
			<>
				{trigger}
				<BottomSheet
					open={open}
					onOpenChange={setOpen}
					title="Date and time"
					headerRight={
						<BottomSheetTextButton strong onClick={() => setOpen(false)}>
							Done
						</BottomSheetTextButton>
					}
					bodyClassName="px-0"
					// The calendar focuses a day as it opens, before the sheet can note
					// its opener, so hand focus back to the trigger here.
					onCloseAutoFocus={(event) => {
						const active = document.activeElement;
						const content = event.currentTarget as Node | null;
						if (active && active !== document.body && !content?.contains(active)) return;
						event.preventDefault();
						triggerRef.current?.focus({ preventScroll: true });
					}}
				>
					{picker}
				</BottomSheet>
			</>
		);
	}

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>{trigger}</PopoverTrigger>
			<PopoverPrimitive.Portal>
				<PopoverPrimitive.Content
					className={cn(
						'z-[9999] w-auto p-0 rounded-md border bg-popover text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-popover-content-transform-origin]',
						// Below 1280px it stays on screen (landscape phones are only
						// ~390px tall) and scrolls inside; opened on a tablet and
						// rotated to a phone, it stacks to fit the width.
						'max-md:max-w-[calc(100vw-1rem)] max-xl:max-h-[var(--radix-popover-content-available-height)] max-xl:overflow-y-auto max-xl:overscroll-contain'
					)}
					sideOffset={8}
					collisionPadding={isTouchViewport() ? 8 : 0}
				>
					{picker}
				</PopoverPrimitive.Content>
			</PopoverPrimitive.Portal>
		</Popover>
	);
};

DateTimeLocalInput.displayName = 'DateTimeLocalInput';
